import { getWordCount } from '@Common/handlers/helper';
import { toggleDrawer } from '@Common/handlers/services';
import { RootState } from '@Common/models/redux/store';
import { Theme, Tooltip, Grid, Typography } from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AbsUnionGuard } from 'src/summarizer/guards/union';
import { filterOutputSummary, getOutputKey } from 'src/summarizer/handlers/logic';
import { OutputAbs } from 'src/summarizer/models/store';
import SummarizerStastDrawer from '../SummarizerStatsDrawer';

const useStyles = makeStyles((theme: Theme) => ({
  dotSeparator: {
    backgroundColor: theme.palette.text.primary,
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    display: 'inline-flex',
    marginBottom: '2px',
    margin: '7px',
  },
  tooltip: {
    fontSize: '13px',
  },
  wordSummary: {
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
}));

function SentenceSummary(props: { sentenceCount: number }) {
  const classes = useStyles();

  const extSentenceSeq = useSelector(
    (state: RootState) => state.smzr.output.ext.summarySentenceSequence
  );
  const extSentences = useSelector((state: RootState) => state.smzr.output.ext.sentences);
  const mode = useSelector((state: RootState) => state.smzr.controls.summaryMode);
  const absLengthGauge = useSelector((state: RootState) => state.smzr.controls.absLengthGauge);

  const absSummary = useSelector((state: RootState) => {
    const outputKey = getOutputKey(mode, absLengthGauge);
    const isAbsType = AbsUnionGuard(state.smzr.output[outputKey]);
    return isAbsType ? (state.smzr.output[outputKey] as OutputAbs).summaryPara : '';
  });

  const [summaryWordCount, setSummaryWordCount] = useState<number>(0);
  const [summarycharacterCount, setSummaryCharacterCount] = useState<number>(0);

  useEffect(() => {
    if (mode === 'abs') {
      setSummaryWordCount(getWordCount(filterOutputSummary(absSummary)));
      setSummaryCharacterCount(filterOutputSummary(absSummary).length);
    }
    if (mode === 'ext') {
      let extSummary = '';
      extSentenceSeq.forEach((sentenceId: keyof typeof extSentences) => {
        extSummary += `${extSentences[sentenceId].text} `;
      });
      setSummaryWordCount(getWordCount(extSummary));
      setSummaryCharacterCount(extSummary.length);
    }
  }, [mode, absSummary, extSentences, extSentenceSeq]);

  return (
    <div style={{ display: 'flex' }}>
      <Typography display="inline" variant="body2" style={{ fontWeight: 600 }}>
        {props.sentenceCount} {props.sentenceCount === 1 ? 'Sentence' : 'Sentences'}
      </Typography>
      <span className={classes.dotSeparator} />
      <Tooltip
        title={`${summarycharacterCount} Characters`}
        arrow
        placement="top"
        classes={{
          tooltip: classes.tooltip,
        }}
      >
        <Grid item>
          <Typography
            display="inline"
            variant="body2"
            className={classes.wordSummary}
            onClick={() => {
              if (summaryWordCount > 0) toggleDrawer('SMZR_STATISTICS', SummarizerStastDrawer);
            }}
          >
            {summaryWordCount} Word
            {summaryWordCount === 1 ? '' : 's'}
          </Typography>
        </Grid>
      </Tooltip>
    </div>
  );
}

export default SentenceSummary;
